import { useNavigate } from "react-router-dom";
import { KWordmark, KMark } from "@/components/KLogo";
import { Bell, Users, ShieldCheck, Sparkles, MessageSquare, Radar, ArrowRight, Building2 } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1786352469165-f9be9b99f3bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const nav = useNavigate();

  const features = [
    { icon: Bell, title: "Situação Agora", desc: "Alertas e avisos do síndico em tempo real, direto na sua tela." },
    { icon: Users, title: "Comunidade privada", desc: "A rede social exclusiva do seu condomínio. Só moradores aprovados." },
    { icon: Radar, title: "Meu Radar", desc: "Escolha o que quer acompanhar e receba só o que importa." },
    { icon: Sparkles, title: "Pergunte ao KONDDO", desc: "Assistente inteligente que responde com base nos documentos do seu prédio." },
    { icon: ShieldCheck, title: "Privacidade e LGPD", desc: "Seus dados financeiros e pessoais nunca ficam expostos." },
    { icon: Building2, title: "Serviços completos", desc: "Reservas, boletos, assembleias e balancete — sem burocracia." },
  ];

  return (
    <div className="min-h-[100dvh] bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <KWordmark size={34} />
          <button
            data-testid="nav-login-btn"
            onClick={() => nav("/entrar")}
            className="text-sm font-medium px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
          >
            Entrar
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 aurora" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="float-in">
            <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-primary mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> A rede privada do seu condomínio
            </span>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Seu condomínio <span className="brand-text">acontece aqui.</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-md">
              Comunicação, comunidade, segurança e inteligência em um único lugar.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="cta-resident"
                onClick={() => nav("/cadastro/morador")}
                className="group flex items-center justify-center gap-2 rounded-full brand-gradient glow-primary px-6 py-3.5 font-semibold text-white active:scale-95 hover:scale-[1.02] transition-transform"
              >
                Sou morador
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="cta-manager"
                onClick={() => nav("/cadastro/sindico")}
                className="flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/15 px-6 py-3.5 font-semibold hover:bg-white/10 transition-colors"
              >
                Sou síndico
              </button>
            </div>
          </div>

          {/* App mock */}
          <div className="relative float-in" style={{ animationDelay: ".1s" }}>
            <div className="absolute -inset-6 brand-gradient opacity-20 blur-3xl rounded-full" />
            <div className="relative mx-auto w-[300px] rounded-[2.5rem] glass-strong border border-white/10 p-4 glow-soft">
              <div className="flex items-center gap-3 mb-4">
                <KMark size={40} />
                <div>
                  <p className="text-xs text-muted-foreground">Boa noite, Igor 👋</p>
                  <p className="text-sm font-semibold font-display">D Garden — Reserva Tatuapé</p>
                </div>
              </div>
              <div className="rounded-2xl p-3 mb-3 border" style={{ background: "hsl(4 90% 58% / 0.1)", borderColor: "hsl(4 90% 58% / 0.35)" }}>
                <p className="text-[11px] font-semibold text-red-400">🔴 ALERTA</p>
                <p className="text-xs mt-1 text-zinc-300">Água da Torre 2 interrompida das 14h às 16h.</p>
              </div>
              <div className="rounded-2xl p-3 mb-3 border border-white/10 bg-white/5">
                <p className="text-[11px] font-semibold text-emerald-400">🟢 TUDO NORMAL</p>
                <p className="text-xs mt-1 text-zinc-300">Elevadores e acessos funcionando.</p>
              </div>
              <div className="rounded-2xl p-3 brand-gradient flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-white" />
                <p className="text-xs font-semibold text-white">Pergunte ao KONDDO</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-3">O que é o KONDDO</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
          Muito mais que gestão. É a comunidade do seu condomínio.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <div key={f.title} className="glass rounded-3xl p-6 card-hover float-in" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-24">
        <div className="relative rounded-[2rem] overflow-hidden border border-white/10">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative p-10 md:p-16 text-center">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">
              A comunidade do seu condomínio <span className="brand-text">começa agora.</span>
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="cta-resident-2" onClick={() => nav("/cadastro/morador")} className="rounded-full brand-gradient glow-primary px-7 py-3.5 font-semibold text-white active:scale-95 hover:scale-[1.02] transition-transform">
                Sou morador
              </button>
              <button data-testid="cta-manager-2" onClick={() => nav("/cadastro/sindico")} className="rounded-full bg-white/10 border border-white/15 px-7 py-3.5 font-semibold hover:bg-white/15 transition-colors">
                Sou síndico
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <KWordmark size={28} />
          <p className="text-xs text-muted-foreground">© 2026 KONDDO — Seu condomínio acontece aqui.</p>
        </div>
      </footer>
    </div>
  );
}
